import React, { FC, useCallback, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import block from 'bem-cn'

import { MODAL_ROUTE_URLS } from 'router/modalRoutes'
import { ROUTE_URLS } from 'router/routes'

import { useHistoryWithState } from 'shared/hooks/useHistoryWithState'
import { IGetUserPromotionsFilters } from 'shared/models/BonusSystem'
import { BonusPromotionStatus, IUserPromotion } from 'shared/models/Promotion'

import {
  changeActiveUserPromotion,
  getMoneyFromPromotion,
  getUserPromotions,
  getUserPromotionsPoll,
} from 'features/bonusSystem/actions'
import {
  selectBonusPromotionsPolling,
  selectChangeActiveIdComm,
  selectGetMoneyFromPromotionIdComm,
} from 'features/bonusSystem/selectors'
import { selectLocaleDict } from 'features/locale/selectors'
import { setIsActivatedFreeSpin } from 'features/slots/actions'

import { useModalContext } from 'components/HOC/withModalQuery/ModalContext'

import Button from 'components/UIKit/Button/Button'

import { currentStatusToArrayOfStatuses } from '../BonusPromotions'

import './BonusActionButton.scss'

interface IBonusActionButtonProps {
  promotion: IUserPromotion
  type?: 'redirect' | 'action'
  filters: IGetUserPromotionsFilters
  className?: string
  promotionIsExpired: boolean
}

const b = block('bonus-action-button')

export const BonusActionButton: FC<IBonusActionButtonProps> = ({
  promotion,
  filters,
  promotionIsExpired,
  type,
  className,
}) => {
  const dispatch = useDispatch()
  const history = useHistoryWithState()
  const { pushNewModal } = useModalContext()

  const promotions = useSelector(selectBonusPromotionsPolling, shallowEqual)
  const locale = useSelector(selectLocaleDict, shallowEqual).bonuses
  const isGetMoneyLoading = useSelector(selectGetMoneyFromPromotionIdComm)[promotion.id]?.isLoading
  const isChangeActiveLoading = useSelector(selectChangeActiveIdComm)[promotion.id]?.isLoading

  const isPromotionAvailable = useMemo(
    () => promotion.status.split('_').includes(BonusPromotionStatus.available),
    [promotion],
  )
  const isPromotionActive = useMemo(() => promotion.status === BonusPromotionStatus.active, [promotion])
  const isPromotionCompleted = useMemo(() => promotion.status === BonusPromotionStatus.completed, [promotion])

  const hasFreeSpins = useMemo(() => !!promotion.freeSpinsCount, [promotion.freeSpinsCount])

  const hasActivatedPromotion = useMemo(() => {
    const activePromotions = promotions.filter((bonus: IUserPromotion) =>
      currentStatusToArrayOfStatuses[BonusPromotionStatus.active].includes(bonus.status),
    )
    return !!activePromotions.length
  }, [promotions])

  const loadPromotions = useCallback(() => {
    dispatch(getUserPromotions(filters))
  }, [dispatch, filters])

  const getMoney = useCallback(() => {
    dispatch(getMoneyFromPromotion({ id: String(promotion.id), callback: loadPromotions }))
  }, [dispatch, loadPromotions, promotion.id])

  const handleActivateUserPromotion = useCallback(() => {
    dispatch(
      changeActiveUserPromotion({
        id: String(promotion.id),
        callback: () => {
          dispatch(getUserPromotionsPoll())
          history.push(`${ROUTE_URLS.MAIN()}?openedModals=%2Fpromotions&status=${BonusPromotionStatus.active}`)
        },
      }),
    )
  }, [dispatch, history, promotion.id])

  const onChangeActive = useCallback(() => {
    if (hasActivatedPromotion) {
      pushNewModal(MODAL_ROUTE_URLS.CONFIRM_ACTIVATE_BONUS(String(promotion.id)))()
    } else {
      handleActivateUserPromotion()
    }
  }, [handleActivateUserPromotion, hasActivatedPromotion, promotion.id, pushNewModal])

  const handleFreeSpinActivation = () => {
    dispatch(setIsActivatedFreeSpin(true))
    history.push(ROUTE_URLS.SLOTS_GAMETYPE('free-spin'))
  }

  const buttonVariant = useMemo(() => {
    if (promotion.status === BonusPromotionStatus.wait && (!type || type === 'action')) {
      return <Button callback={getMoney} text={locale.collectWinnings} loading={isGetMoneyLoading} />
    }

    if (isPromotionAvailable && (!type || type === 'action')) {
      return <Button callback={onChangeActive} text={locale.activate} loading={isChangeActiveLoading} />
    }

    if (isPromotionActive && hasFreeSpins && (!type || type === 'redirect')) {
      return <Button callback={handleFreeSpinActivation} text={locale.freeSpinsGames} />
    }

    if (isPromotionCompleted && (!type || type === 'redirect')) {
      return <Button callback={() => history.push(ROUTE_URLS.SLOTS_GAMETYPE('wagering'))} text={locale.openGames} />
    }

    return null
  }, [
    getMoney,
    hasFreeSpins,
    history,
    isChangeActiveLoading,
    isGetMoneyLoading,
    isPromotionActive,
    isPromotionAvailable,
    isPromotionCompleted,
    locale,
    onChangeActive,
    promotion.status,
    type,
  ])

  return buttonVariant && !promotionIsExpired ? <div className={b.mix(className)}>{buttonVariant}</div> : null
}
